import { useState, memo } from 'react';
import { getAvatarUrl } from '@/lib/avatars';
import { Link } from 'react-router-dom';
import { MoreHorizontal, Trash2, Flag, Reply, ChevronDown, ChevronUp, SmilePlus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LinkifiedText } from './LinkifiedText';
import { Comment } from '@/hooks/useComments';
import { useAuth } from '@/hooks/useAuth';
import { useLongPress } from '@/hooks/useLongPress';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { COMMENT_REACTIONS, type CommentReactionSummary } from '@/hooks/useCommentReactions';

interface CommentItemProps {
  comment: Comment;
  reactions?: CommentReactionSummary[];
  onDelete: (commentId: string) => Promise<boolean>;
  onReport: (commentId: string) => void;
  onReply: (commentId: string, username: string) => void;
  onReaction?: (commentId: string, emoji: string) => void;
  getReactionsForComment?: (commentId: string) => CommentReactionSummary[];
  depth?: number;
}

const MAX_DEPTH = 3;

function CommentItemComponent({ 
  comment, 
  reactions = [],
  onDelete, 
  onReport, 
  onReply, 
  onReaction,
  getReactionsForComment,
  depth = 0 
}: CommentItemProps) {
  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  
  const isOwner = user?.id === comment.user_id;
  // Comments are never anonymous - always show the commenter's identity
  const displayName = comment.profile?.display_name || 'User';
  const avatarUrl = getAvatarUrl(comment.profile?.avatar_url, comment.user_id);
  const initials = displayName.charAt(0).toUpperCase();
  const hasReplies = comment.replies && comment.replies.length > 0;

  // Long press to show reaction picker on mobile
  const longPressHandlers = useLongPress({
    onLongPress: () => setShowReactionPicker(true),
  });

  const handleDelete = async () => {
    setIsDeleting(true);
    await onDelete(comment.id);
    setIsDeleting(false);
  };

  const handleReaction = (emoji: string) => {
    onReaction?.(comment.id, emoji);
    setShowReactionPicker(false);
  };

  const timeAgo = formatDistanceToNow(new Date(comment.created_at), { addSuffix: true });

  return (
    <div className={cn("relative", depth > 0 && "ml-8 pl-4 border-l-2 border-muted")}>
      <div className="flex gap-3 py-3" {...longPressHandlers}>
        <Link to={`/user/${comment.user_id}`}>
          <Avatar className={cn("flex-shrink-0", depth > 0 ? "h-7 w-7" : "h-8 w-8")}>
            <AvatarImage src={avatarUrl || undefined} alt={displayName} />
            <AvatarFallback className="bg-muted" />
          </Avatar>
        </Link>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{displayName}</span>
            <span className="text-xs text-muted-foreground">{timeAgo}</span>
          </div>
          
          <div className="mt-1 text-sm text-foreground break-words">
            <LinkifiedText text={comment.content} />
          </div>

          {/* Reactions display */}
          <AnimatePresence>
            {reactions.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex flex-wrap gap-1 mt-2"
              >
                {reactions.map((reaction) => (
                  <button
                    key={reaction.emoji}
                    onClick={() => onReaction?.(comment.id, reaction.emoji)}
                    className={cn(
                      "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors",
                      reaction.hasReacted
                        ? "bg-primary/10 border-primary/30 text-primary"
                        : "bg-muted border-border hover:border-primary/30"
                    )}
                  >
                    <span>{reaction.emoji}</span>
                    <span className="font-medium">{reaction.count}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Action buttons */}
          <div className="flex items-center gap-4 mt-2">
            {/* Reaction picker button */}
            {onReaction && (
              <Popover open={showReactionPicker} onOpenChange={setShowReactionPicker}>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                    <SmilePlus className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2 z-[60]" side="top" align="start">
                  <div className="flex gap-1">
                    {COMMENT_REACTIONS.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => handleReaction(emoji)}
                        className="text-xl hover:scale-125 transition-transform p-1"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            {/* Reply button */}
            {depth < MAX_DEPTH && (
              <button
                onClick={() => onReply(comment.id, displayName)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <Reply className="h-3 w-3" />
                Reply
              </button>
            )}
          </div>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7 flex-shrink-0"
              disabled={isDeleting}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isOwner && (
              <DropdownMenuItem 
                onClick={handleDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            )}
            {!isOwner && (
              <DropdownMenuItem onClick={() => onReport(comment.id)}>
                <Flag className="h-4 w-4 mr-2" />
                Report
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Replies */}
      {hasReplies && (
        <div>
          <button
            onClick={() => setShowReplies(!showReplies)}
            className="flex items-center gap-1 ml-11 mb-2 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            {showReplies ? (
              <>
                <ChevronUp className="h-3 w-3" />
                Hide {comment.replies!.length} {comment.replies!.length === 1 ? 'reply' : 'replies'}
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                Show {comment.replies!.length} {comment.replies!.length === 1 ? 'reply' : 'replies'}
              </>
            )}
          </button>
          
          {showReplies && (
            <div>
              {comment.replies!.map(reply => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  reactions={getReactionsForComment ? getReactionsForComment(reply.id) : []}
                  onDelete={onDelete}
                  onReport={onReport}
                  onReply={onReply}
                  onReaction={onReaction}
                  getReactionsForComment={getReactionsForComment}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Memoize to prevent unnecessary re-renders in nested reply trees
export const CommentItem = memo(CommentItemComponent);
